import type { Produto } from '@/features/produto/types/Produto';
import type { ProdutoEstoque } from '@/features/produto/types/ProdutoEstoque';
import type { ProdutoLote } from '@/features/produto/types/ProdutoLote';
import type { ProdutoLoteEstoque } from '@/features/produto/types/ProdutoLoteEstoque';
import type { ProdutoPacoteBarra } from '@/features/produto/types/ProdutoPacoteBarra';
import { ProdutoLocalEstoqueStatus } from '@/features/produto-local-estoque/types/enum';
import { err, ok, type Result } from '@/std/result';
import type { ProdutoConfig } from './config';

export type ProdutoEstoqueRegistro = ProdutoEstoque | ProdutoLoteEstoque;

export type ProdutoEstoqueSelecao = {
    produto: Pick<Produto, 'pro_idpk' | 'produto_estoque'> | null | undefined;
    pacoteBarra?: Pick<ProdutoPacoteBarra, 'pcb_multiplicador' | 'pcb_estoque_idpk'> | null;
    lote?: Pick<ProdutoLote, 'prl_idpk' | 'estoque'> | null;
    localEstoque?: ProdutoEstoqueRegistro | null;
    /** Permite usar o multiplicador registrado no documento. */
    multiplicador?: number | null;
};

export type ProdutoEstoqueResumo = Readonly<{
    localEstoque: ProdutoEstoqueRegistro | null;
    produtoIdpk: number | null;
    loteIdpk: string | null;
    /** Preserva a seleção do local mesmo quando seu saldo não foi retornado. */
    localEstoqueIdpk: number | null;
    multiplicador: number;
    /** Saldo na unidade base do produto. */
    estoqueBase: number;
    /** Saldo convertido para a unidade da embalagem. */
    estoqueDisponivel: number;
}>;

/**
 * Retorna o ID do local de estoque, distinto do ID do registro de saldo (pre_idpk/ple_idpk).
 *
 * @param estoque Registro de saldo do produto ou de um lote.
 * @returns ID do local, ou `null` quando o saldo não possui local associado.
 *
 * @example
 * // Para um registro com pre_idpk = 10 e pre_estoque_idpk = 2:
 * getProdutoEstoqueId(estoqueProduto); // 2
 * // Para um registro com ple_idpk = 20 e ple_estoque_idpk = 3:
 * getProdutoEstoqueId(estoqueLote); // 3
 */
export function getProdutoEstoqueId(estoque: ProdutoEstoqueRegistro): number | null {
    return 'ple_estoque_idpk' in estoque ? estoque.ple_estoque_idpk : estoque.pre_estoque_idpk;
}

/**
 * Seleciona o saldo do lote, quando informado, ou do produto.
 * Prioriza o ID do local informado; sem seleção explícita, seleciona automaticamente um único saldo.
 * Considera apenas saldos permitidos para venda e mantém o último registro elegível de cada local.
 *
 * @param selecao Produto, lote opcional e local de estoque escolhido.
 * @returns Saldo selecionado, ou `undefined` sem produto, sem saldos ou sem local correspondente.
 *
 * @example
 * // Considerando um produto com apenas um registro em produto_estoque:
 * getProdutoEstoqueSelecionado({ produto }); // produto.produto_estoque[0]
 * getProdutoEstoqueSelecionado({ produto: null }); // undefined
 *
 * @example
 * // Com vários saldos, seleciona o registro cujo ID de local corresponde a localEstoque:
 * getProdutoEstoqueSelecionado({ produto, localEstoque });
 */
export function getProdutoEstoqueSelecionado({
    produto,
    lote,
    localEstoque,
}: ProdutoEstoqueSelecao): ProdutoEstoqueRegistro | undefined {
    if (!produto) {
        return undefined;
    }

    const estoques = getProdutoEstoquesParaVenda(produto, lote);
    if (!estoques.length) {
        return undefined;
    }

    if (localEstoque) {
        return estoques.findLast(
            (item) => getProdutoEstoqueId(item) === getProdutoEstoqueId(localEstoque),
        );
    }

    return estoques.length === 1 ? estoques[0] : undefined;
}

/**
 * Resume a seleção e converte o saldo da unidade base para a unidade da embalagem.
 * Usa o primeiro multiplicador diferente de zero entre o explícito, o da embalagem e 1.
 * Sem saldo selecionado, retorna saldo zero e preserva o ID do local informado.
 *
 * @param selecao Produto, embalagem, lote, local e multiplicador a considerar.
 * @returns Identificadores da seleção, saldo base e saldo disponível dividido pelo multiplicador.
 *
 * @example
 * // Considerando um produto com um único saldo disponível de 24 unidades base:
 * const resumo = getProdutoEstoque({ produto, multiplicador: 6 });
 * resumo.estoqueBase; // 24
 * resumo.estoqueDisponivel; // 4 embalagens
 *
 * @example
 * getProdutoEstoque({ produto: null }).estoqueDisponivel; // 0
 */
export function getProdutoEstoque(selecao: ProdutoEstoqueSelecao): ProdutoEstoqueResumo {
    const localEstoque = getProdutoEstoqueSelecionado(selecao);
    const referenciaLocal = localEstoque ?? selecao.localEstoque;
    const multiplicador = selecao.multiplicador || selecao.pacoteBarra?.pcb_multiplicador || 1;
    const estoqueBase = localEstoque
        ? 'ple_estoque_disponivel' in localEstoque
            ? localEstoque.ple_estoque_disponivel
            : localEstoque.pre_estoque_disponivel
        : 0;

    return {
        estoqueBase,
        estoqueDisponivel: estoqueBase / multiplicador,
        localEstoque: localEstoque ?? null,
        localEstoqueIdpk: referenciaLocal ? getProdutoEstoqueId(referenciaLocal) : null,
        loteIdpk: selecao.lote?.prl_idpk ?? null,
        multiplicador,
        produtoIdpk: selecao.produto?.pro_idpk ?? null,
    };
}

/**
 * Lista os saldos do lote, quando informado, ou do produto que podem ser usados na venda.
 * Aceita saldos sem local associado ou com local ativo que permite venda e remove
 * duplicatas por ID do local, mantendo o último registro.
 *
 * @param produto Produto cujos saldos serão consultados quando não houver lote.
 * @param lote Lote cujos saldos substituem os saldos do produto na consulta.
 * @returns Saldos permitidos para venda, sem IDs de local repetidos.
 *
 * @example
 * getProdutoEstoquesParaVenda({ produto_estoque: [] }); // []
 *
 * @example
 * // Consulta os saldos do lote em vez dos saldos do produto:
 * getProdutoEstoquesParaVenda(produto, lote);
 */
export function getProdutoEstoquesParaVenda(
    produto: Pick<Produto, 'produto_estoque'>,
    lote: ProdutoEstoqueSelecao['lote'] = null,
): ProdutoEstoqueRegistro[] {
    const origem = lote ? (lote.estoque ?? []) : (produto.produto_estoque ?? []);

    const estoques = origem.filter(
        (estoque) =>
            estoque.estoque === null ||
            (estoque.estoque?.est_permite_venda === 'S' &&
                estoque.estoque.est_status === ProdutoLocalEstoqueStatus.Ativado),
    );

    return [
        ...new Map(estoques.map((estoque) => [getProdutoEstoqueId(estoque), estoque])).values(),
    ];
}

/**
 * Valida a quantidade solicitada conforme as regras de estoque e fracionamento do produto.
 * A quantidade padrão é 0.1 para venda fracionada e 1 nos demais casos. Quantidades não
 * positivas são rejeitadas; sem controle de estoque, as demais validações são dispensadas.
 *
 * @param parametros Configuração do produto e saldos disponível e solicitado na mesma unidade.
 * @returns Sucesso com valor `null`, ou erro com a razão que impede a venda.
 *
 * @example
 * const produtoConfig = {
 *     ...getProdutoConfigDefault(),
 *     permiteEstoqueNegativo: false,
 *     permiteVendaFracionada: false,
 * };
 * produtoPodeSerVendido({ produtoConfig, estoqueDisponivel: 2, estoqueSolicitado: 1 }).ok;
 * // true
 * const resultado = produtoPodeSerVendido({
 *     produtoConfig,
 *     estoqueDisponivel: 2,
 *     estoqueSolicitado: 3,
 * });
 * resultado.error?.message; // 'Estoque insuficiente para a quantidade solicitada.'
 */
export function produtoPodeSerVendido({
    produtoConfig,
    estoqueDisponivel,
    estoqueSolicitado,
}: {
    produtoConfig: ProdutoConfig;
    estoqueDisponivel: number;
    estoqueSolicitado?: number;
}): Result<null> {
    const { controlaEstoque, permiteEstoqueNegativo, permiteVendaFracionada } = produtoConfig;

    const qtde = estoqueSolicitado ?? (permiteVendaFracionada ? 0.1 : 1);

    if (qtde <= 0) {
        return err('A quantidade deve ser maior que zero.');
    }

    // Se o produto não controla estoque então ele pode ser vendido
    if (!controlaEstoque) {
        return ok(null);
    }

    // Se não pode venda fracionada e a quantidade é decimal então retorna um erro
    if (!permiteVendaFracionada && !Number.isInteger(qtde)) {
        return err('O produto não permite venda fracionada.');
    }

    // Se o produto controla o estoque e permite ficar com o estoque negativo então ele também pode ser vendido
    if (permiteEstoqueNegativo) {
        return ok(null);
    }

    if (!(qtde <= estoqueDisponivel)) {
        return err('Estoque insuficiente para a quantidade solicitada.');
    }

    return ok(null);
}
